import { Avatar, Box, Typography } from '@mui/material'
import StorefrontIcon from '@mui/icons-material/Storefront'
import { styled } from '@mui/material/styles'

import { useFormatMessage } from '@/translations/useFormatMessage'
import { colors, radius, spacing, typography, zIndex } from '@/styles/designTokens'

const Overlay = styled(Box)({
	position: 'fixed',
	inset: 0,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	backgroundColor: colors.overlayBackdrop,
	zIndex: zIndex.storeClosedOverlay,
	pointerEvents: 'auto',
})

const Card = styled(Box)({
	backgroundColor: colors.surfaceWhite,
	borderRadius: radius.card,
	boxShadow:
		'0px 1px 3px rgba(0,0,0,0.2), 0px 2px 1px rgba(0,0,0,0.12), 0px 1px 1px rgba(0,0,0,0.14)',
	padding: spacing.xl,
	maxWidth: 400,
	width: '100%',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	gap: spacing.md,
})

const StatusPill = styled(Box)({
	display: 'flex',
	alignItems: 'center',
	gap: spacing.xs,
	backgroundColor: colors.errorAltLight,
	borderRadius: radius.pill,
	padding: `${spacing.xs}px ${spacing.md}px`,
})

/**
 * Props for the StoreClosed overlay modal component.
 */
interface StoreClosedProps {
	/** Store name displayed in personalized closure message. If omitted, shows generic message. */
	locationName?: string
	/** Full address of the closed store (e.g., "123 Main St, New York, NY") */
	address?: string
	/** Reason for closure. If omitted, defaults to fallback message. */
	message?: string | null
}

/**
 * StoreClosed displays a fixed overlay modal when a store is closed.
 * The overlay appears on top of all menu content (z-index: 1300) without navigating away.
 * Used by MenuLayout when `isStoreClosureActive` is true.
 *
 * @component
 * @example
 * <StoreClosed
 *   locationName="Downtown Location"
 *   address="123 Main St, New York, NY"
 *   message="Closed for renovation"
 * />
 */
export function StoreClosed({ locationName, address, message }: StoreClosedProps) {
	const { t } = useFormatMessage()

	const header = locationName
		? t('component:storeClosed:header:withName', { locationName })
		: t('component:storeClosed:header:generic')

	const body = message ?? t('component:storeClosed:message:fallback')

	return (
		<Overlay role="main" aria-labelledby="store-closed-title">
			<Card>
				<Avatar sx={{ width: 64, height: 64 }} alt={locationName ?? ''} />

				<Box sx={{ textAlign: 'center', width: '100%' }}>
					<Typography
						id="store-closed-title"
						sx={{
							fontWeight: typography.fontWeightSemiBold,
							fontSize: typography.fontSizeXl,
							lineHeight: '28px',
							letterSpacing: '0.18px',
							color: colors.textPrimary,
						}}
					>
						{header}
					</Typography>
					{address && (
						<Typography
							sx={{
								fontWeight: typography.fontWeightRegular,
								fontSize: typography.fontSizeMd,
								lineHeight: '20px',
								letterSpacing: '0.25px',
								color: colors.textSecondary,
								mt: '4px',
							}}
						>
							{address}
						</Typography>
					)}
				</Box>

				<StatusPill>
					<StorefrontIcon sx={{ fontSize: 24, color: colors.errorAlt }} aria-hidden />
					<Typography
						sx={{
							fontWeight: typography.fontWeightBold,
							fontSize: typography.fontSizeMd,
							lineHeight: '20px',
							letterSpacing: '0.25px',
							color: colors.errorAlt,
							whiteSpace: 'nowrap',
						}}
					>
						{t('component:storeClosed:status')}
					</Typography>
				</StatusPill>

				<Typography
					sx={{
						fontSize: typography.fontSizeLg,
						lineHeight: '24px',
						letterSpacing: '0.25px',
						color: colors.textPrimary,
						textAlign: 'left',
						width: '100%',
					}}
				>
					{body}
				</Typography>
			</Card>
		</Overlay>
	)
}
